import type { DrawerRootActions } from '@base-ui/react';
import {
  FuuzeButton,
  FuuzeColors,
  FuuzeDrawer,
  FuuzeTimeline,
  FuuzeTooltip,
  type SpotlightLink,
  useListSpotlights,
} from '@fuuze/shared';
import { type CSSProperties, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TbLayoutSidebarRightExpand } from 'react-icons/tb';
import { useShallow } from 'zustand/react/shallow';
import { usePlayerStore } from '../../store/player.store';
import { SpotlightCard } from '../spotlight-card';

type Props = {
  itemId: string;
};

export function PlayerSpotlightsDrawer({ itemId }: Props) {
  const { t } = useTranslation();
  const drawerRef = useRef<DrawerRootActions>(null);
  const [seek, playPause] = usePlayerStore(useShallow(state => [state.seek, state.playPause]));

  const spotlights = useListSpotlights(itemId);

  const onPlay = async (spotlight: SpotlightLink) => {
    drawerRef.current?.close();
    await seek(spotlight.start);
    await playPause();
  };

  if (spotlights.data?.links.length === 0) {
    return null;
  }

  return (
    <FuuzeDrawer.Root actionsRef={drawerRef} swipeDirection='right'>
      <FuuzeDrawer.Trigger
        render={props => (
          <FuuzeTooltip
            render={
              <FuuzeButton outline scaleOnHover {...props} style={styles.drawerButton}>
                <TbLayoutSidebarRightExpand size={24} strokeWidth={1.5} />
                <span style={styles.drawerButtonText}>{t('common.spotlight_other')}</span>
              </FuuzeButton>
            }
          >
            {t('components.playerCollapsed.spotlightsDrawer.tooltip')}
          </FuuzeTooltip>
        )}
      />
      <FuuzeDrawer.Portal>
        <FuuzeDrawer.Backdrop />
        <FuuzeDrawer.Viewport>
          <FuuzeDrawer.Popup style={styles.drawerPopup}>
            <FuuzeDrawer.Content>
              <FuuzeDrawer.Close style={styles.drawerClose} />
              <FuuzeDrawer.Title>{t('common.spotlight', { count: 2 })}</FuuzeDrawer.Title>

              <FuuzeTimeline
                align='left'
                value={spotlights.data?.links}
                content={(item: SpotlightLink) => <SpotlightCard spotlight={item} onPlay={onPlay} />}
                pt={{
                  content: {
                    style: styles.drawerContent,
                  },
                }}
              />
            </FuuzeDrawer.Content>
          </FuuzeDrawer.Popup>
        </FuuzeDrawer.Viewport>
      </FuuzeDrawer.Portal>
    </FuuzeDrawer.Root>
  );
}

const styles = {
  drawerButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '.125rem .25rem',
    gap: '.25rem',
    borderColor: '#764211',
  },
  drawerButtonText: {
    fontSize: 'small',
  },
  drawerPopup: {
    borderLeft: `1px solid ${FuuzeColors.dark.dark200}`,
  },
  drawerClose: {
    marginLeft: 'auto',
  },
  drawerContent: {
    paddingBottom: '1rem',
  },
} satisfies Record<string, CSSProperties>;
